import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "initialState",
    "expandedState",
    "requirementsContent",
    "accordionIcon",
    "uploadArea",
    "uploadedFiles",
    "continueSection",
    "plaidFailedBanner"
  ]

  static values = {
    applicationId: String,
    statementsCount: { type: Number, default: 0 },
    requirementsExpanded: { type: Boolean, default: true }
  }

  connect() {
    this.initializeState()

    // Listen for successful uploads to refresh the page
    document.addEventListener("turbo:submit-end", this.handleUploadComplete.bind(this))
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this.handleUploadComplete.bind(this))
  }

  initializeState() {
    // If user already has statements uploaded, show expanded state
    if (this.statementsCountValue > 0) {
      this.expandManual()
    }

    // Initialize requirements accordion state
    if (this.requirementsExpandedValue) {
      this.showRequirements()
    } else {
      this.hideRequirements()
    }
  }

  showPlaidFailedBanner(message) {
    if (this.hasPlaidFailedBannerTarget) {
      this.plaidFailedBannerTarget.classList.remove("hidden")
      const messageEl = this.plaidFailedBannerTarget.querySelector("[data-plaid-error-message]")
      if (messageEl) {
        messageEl.textContent = message
      }
    }
  }

  expandManual() {
    if (this.hasInitialStateTarget) {
      this.initialStateTarget.classList.add("hidden")
    }
    if (this.hasExpandedStateTarget) {
      this.expandedStateTarget.classList.remove("hidden")
    }

    // Scroll to upload area smoothly
    if (this.hasUploadAreaTarget) {
      setTimeout(() => {
        this.uploadAreaTarget.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 100)
    }
  }

  collapseManual() {
    if (this.hasExpandedStateTarget) {
      this.expandedStateTarget.classList.add("hidden")
    }
    if (this.hasInitialStateTarget) {
      this.initialStateTarget.classList.remove("hidden")
    }
  }

  toggleRequirements() {
    this.requirementsExpandedValue = !this.requirementsExpandedValue

    if (this.requirementsExpandedValue) {
      this.showRequirements()
    } else {
      this.hideRequirements()
    }
  }

  showRequirements() {
    if (this.hasRequirementsContentTarget) {
      this.requirementsContentTarget.classList.remove("hidden")
    }
    if (this.hasAccordionIconTarget) {
      this.accordionIconTarget.classList.add("rotate-180")
    }
  }

  hideRequirements() {
    if (this.hasRequirementsContentTarget) {
      this.requirementsContentTarget.classList.add("hidden")
    }
    if (this.hasAccordionIconTarget) {
      this.accordionIconTarget.classList.remove("rotate-180")
    }
  }

  // Handle continue button click - navigate to next step
  continueToNextStep() {
    // Find the multi-step form controller and trigger next step
    const formElement = this.element.closest("[data-controller~='multi-step-form']")

    if (formElement) {
      const multiStepController = this.application.getControllerForElementAndIdentifier(
        formElement,
        "multi-step-form"
      )

      if (multiStepController && typeof multiStepController.nextStep === "function") {
        multiStepController.nextStep()
      } else {
        // Fallback: submit the form with forward direction
        this.submitFormForward()
      }
    } else {
      // Fallback: submit the form with forward direction
      this.submitFormForward()
    }
  }

  submitFormForward() {
    // Find the form and submit it
    const form = this.element.closest("form")
    if (form) {
      // Add direction input if not exists
      let directionInput = form.querySelector('input[name="direction"]')
      if (!directionInput) {
        directionInput = document.createElement("input")
        directionInput.type = "hidden"
        directionInput.name = "direction"
        form.appendChild(directionInput)
      }
      directionInput.value = "forward"

      // Submit the form
      form.requestSubmit()
    }
  }

  handleUploadComplete(event) {
    // Check if this was a document upload
    const response = event.detail?.fetchResponse?.response
    if (response && response.ok) {
      // The page will be reloaded by the document upload controller
      // This is just a backup handler
    }
  }
}
